import { toast } from "sonner";
import i18n from "../../../i18n";

type TransactionHandlerCommonOptions = {
  onStart?: (transactionId: string, rowIndex?: number) => void;
  onSuccess?: (transactionId: string, rowIndex?: number) => void | Promise<void>;
  onError?: (message: string, err: any) => void;
  onFinally?: (transactionId: string, rowIndex?: number) => void;
};

type ApproveOptions = TransactionHandlerCommonOptions & {
  resolvePaymentId: () => Promise<string | null>;
  approveMutation: (transactionId: string, paymentId: string) => Promise<unknown>;
  onMissingPaymentMethod?: (message: string) => void;
};

type RejectOptions = TransactionHandlerCommonOptions & {
  rejectMutation: (transactionId: string) => Promise<unknown>;
};

export const createApproveTransactionHandler =
  ({
    resolvePaymentId,
    approveMutation,
    onStart,
    onSuccess,
    onError,
    onFinally,
    onMissingPaymentMethod,
  }: ApproveOptions) =>
  async (transactionId: string, rowIndex?: number) => {
    if (!transactionId) {
      const message = i18n.t("lifestyleTransactions.toasts.missingId");
      if (onError) {
        onError(message, new Error(message));
      } else {
        toast.error(message);
      }
      return;
    }

    const paymentId = await resolvePaymentId();
    if (!paymentId) {
      const message = i18n.t("lifestyleTransactions.toasts.missingPayment");
      if (onMissingPaymentMethod) {
        onMissingPaymentMethod(message);
      } else if (onError) {
        onError(message, new Error(message));
      } else {
        toast.error(message);
      }
      return;
    }

    onStart?.(transactionId, rowIndex);

    try {
      await approveMutation(transactionId, paymentId);
      toast.success(i18n.t("lifestyleTransactions.toasts.approveSuccess"));
      await onSuccess?.(transactionId, rowIndex);
    } catch (err: any) {
      const message = err?.message || i18n.t("lifestyleTransactions.toasts.approveFailed");
      if (onError) {
        onError(message, err);
      } else {
        toast.error(message);
      }
    } finally {
      onFinally?.(transactionId, rowIndex);
    }
  };

export const createRejectTransactionHandler =
  ({ rejectMutation, onStart, onSuccess, onError, onFinally }: RejectOptions) =>
  async (transactionId: string, rowIndex?: number) => {
    if (!transactionId) {
      const message = i18n.t("lifestyleTransactions.toasts.missingId");
      if (onError) {
        onError(message, new Error(message));
      } else {
        toast.error(message);
      }
      return;
    }

    onStart?.(transactionId, rowIndex);

    try {
      await rejectMutation(transactionId);
      toast.success(i18n.t("lifestyleTransactions.toasts.rejectSuccess"));
      await onSuccess?.(transactionId, rowIndex);
    } catch (err: any) {
      const message = err?.message || i18n.t("lifestyleTransactions.toasts.rejectFailed");
      if (onError) {
        onError(message, err);
      } else {
        toast.error(message);
      }
    } finally {
      onFinally?.(transactionId, rowIndex);
    }
  };
